import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowLeft, Plus } from 'lucide-react'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  QueryErrorState,
  SearchInput,
  Select,
  Toolbar,
  ToolbarCount,
  ToolbarLeft,
  ToolbarRight,
} from '@/components/ui'
import { useLibrary, useQuestionnaires } from '../api/queries'
import type { LibraryEntry } from '../api/types'
import { EntryDrawer } from '../components/EntryDrawer'
import { CHOICE_LABEL, formatDate } from '../utils/meta'

type Filter = 'active' | 'review_due' | 'all'

/** /regulatory/questionnaires/library — the approved answers questionnaires are filled from. */
export function AnswerLibraryPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [filter, setFilter] = useState<Filter>('active')
  const [openId, setOpenId] = useState<string | 'new' | null>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 200)
    return () => clearTimeout(id)
  }, [search])

  const { data, isLoading, isError, error, refetch } = useLibrary({
    q: debounced || undefined,
    review_due: filter === 'review_due',
    include_inactive: filter === 'all',
  })
  const rows = useMemo(() => data?.items ?? [], [data])
  const { data: waiting } = useQuestionnaires({ mode: 'import' })
  const toReview = (waiting?.items ?? []).filter((r) => r.stage === 'in_review')
  const stillReading = (waiting?.items ?? []).filter(
    (r) => r.stage === 'extracting' || r.stage === 'matching',
  )
  const failed = (waiting?.items ?? []).filter((r) => r.stage === 'failed')
  const canEdit = data?.can_edit ?? false
  const dueCount = rows.filter(
    (r) => r.volatile && (!r.review_due || new Date(r.review_due) <= new Date()),
  ).length

  const columns = useMemo<ColumnDef<LibraryEntry, unknown>[]>(
    () => [
      {
        id: 'question',
        header: 'Question',
        accessorKey: 'canonical_question',
        cell: (ctx) => (
          <span className="flex items-center gap-2">
            <span className="text-role-data-primary">{ctx.row.original.canonical_question}</span>
            {!ctx.row.original.active ? <Chip size="xs">Inactive</Chip> : null}
          </span>
        ),
      },
      {
        id: 'answer',
        header: 'Answer',
        accessorFn: (r) => r.answer_text ?? '',
        enableSorting: false,
        cell: (ctx) => {
          const r = ctx.row.original
          return (
            <span className="line-clamp-2 text-secondary">
              {r.answer_choice ? <strong>{CHOICE_LABEL[r.answer_choice]} — </strong> : null}
              {r.answer_text || '—'}
            </span>
          )
        },
      },
      {
        id: 'use_count',
        header: 'Used',
        accessorKey: 'use_count',
        size: 70,
      },
      {
        id: 'variant_count',
        header: 'Wordings',
        accessorFn: (r) => r.variant_count ?? 0,
        size: 90,
      },
      {
        id: 'review',
        header: 'Check by',
        accessorFn: (r) => r.review_due ?? '',
        cell: (ctx) => {
          const r = ctx.row.original
          if (!r.volatile) return <span className="text-tertiary">—</span>
          const due = !r.review_due || new Date(r.review_due) <= new Date()
          return (
            <Chip size="xs" variant={due ? 'warning' : 'neutral'}>
              {r.review_due ? formatDate(r.review_due) : 'Set a date'}
            </Chip>
          )
        },
      },
      {
        id: 'approved_at',
        header: 'Approved',
        accessorFn: (r) => r.approved_at ?? '',
        cell: (ctx) => formatDate(ctx.row.original.approved_at),
      },
    ],
    [],
  )

  return (
    <div data-density="comfortable" className="flex h-full flex-col">
      <PageHeaderBand
        title="Answer Library"
        description="SPI's approved answers. Every approved questionnaire adds to it; new questionnaires are answered from it."
      >
        <MetricStrip variant="inline" className="ml-auto">
          <MetricCard variant="inline" label="Answers" value={rows.length.toLocaleString()} />
          <MetricCard variant="inline" label="Due for a check" value={dueCount.toLocaleString()} />
        </MetricStrip>
      </PageHeaderBand>

      <div className="flex min-h-0 flex-1 flex-col">
        {toReview.length || stillReading.length || failed.length ? (
          <div className="border-b border-info-border bg-info-bg px-4 py-2 text-sm text-info-text">
            {toReview.length
              ? `${toReview.length} imported questionnaire${toReview.length === 1 ? ' is' : 's are'} waiting for your review. Answers reach the library only when you open one and click Add to library. `
              : ''}
            {stillReading.length ? `${stillReading.length} still being read. ` : ''}
            {failed.length ? `${failed.length} failed to read — open it and Retry. ` : ''}
            <button
              type="button"
              className="underline"
              onClick={() => navigate({ to: '/regulatory/questionnaires' })}
            >
              Go to questionnaires
            </button>
          </div>
        ) : null}
        <Toolbar>
          <Button variant="ghost" onClick={() => navigate({ to: '/regulatory/questionnaires' })}>
            <ArrowLeft size={14} /> Questionnaires
          </Button>
          <SearchInput
            containerClassName="flex-1 max-w-none"
            placeholder="Search questions, wordings and answers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search"
          />
          {canEdit ? (
            <Button variant="primary" onClick={() => setOpenId('new')}>
              <Plus size={14} /> New answer
            </Button>
          ) : null}
        </Toolbar>
        <Toolbar>
          <ToolbarLeft>
            <Select
              aria-label="Show"
              value={filter}
              onChange={(e) => setFilter(e.target.value as Filter)}
            >
              <option value="active">Active answers</option>
              <option value="review_due">Due for a check</option>
              <option value="all">Including inactive</option>
            </Select>
          </ToolbarLeft>
          <ToolbarRight>
            <ToolbarCount>{rows.length.toLocaleString()} answers</ToolbarCount>
          </ToolbarRight>
        </Toolbar>

        <div className="flex-1 min-h-0 overflow-auto bg-canvas p-4">
          {isError ? (
            <QueryErrorState
              error={error}
              onRetry={() => void refetch()}
              subject="answer library"
            />
          ) : rows.length === 0 && !isLoading ? (
            <EmptyState
              variant={debounced ? 'no-match' : 'no-data'}
              title={debounced ? 'No answers match' : 'The library is empty'}
              description="Import questionnaires SPI already answered (Upload questionnaire → One SPI already answered) to fill it."
            />
          ) : (
            <DataTable
              data={rows}
              columns={columns}
              getRowId={(r) => r.id}
              onRowClick={(r) => setOpenId(r.id)}
              moduleId="questionnaires-library"
              maxHeight="100%"
              minTableWidth={900}
            />
          )}
        </div>
      </div>

      <EntryDrawer entryId={openId} canEdit={canEdit} onClose={() => setOpenId(null)} />
    </div>
  )
}
